import React, { useState } from "react";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { toast } from "react-toastify";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "92%",
  maxWidth: 420,
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "#FFFFFF",
  borderRadius: "20px",
  boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
  outline: "none",
  p: "24px",
};

export default function AddCardSavings({ open, handleClose, onCardAdded }) {
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvv, setCvv] = useState("");
  const [cardName, setCardName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Format Card Number
  const handleCardNumberChange = (e) => {
    const rawValue = e.target.value.replace(/\D/g, "").slice(0, 16);
    const formatted = rawValue.replace(/(.{4})/g, "$1 ").trim();
    setCardNumber(formatted);
  };

  // Format Expiry (MM/YY)
  const handleExpiryChange = (e) => {
    let rawValue = e.target.value.replace(/\D/g, "").slice(0, 4);
    if (rawValue.length > 2) {
      rawValue = `${rawValue.slice(0, 2)}/${rawValue.slice(2)}`;
    }
    setExpiry(rawValue);
  };

  // Format CVV
  const handleCvvChange = (e) => {
    const rawValue = e.target.value.replace(/\D/g, "").slice(0, 3);
    setCvv(rawValue);
  };

  // Detect card type
  const getCardBrand = (number) => {
    const cleanNum = number.replace(/\s/g, "");
    if (/^4/.test(cleanNum)) return "visa";
    if (/^(5[1-5]|2[2-7])/.test(cleanNum)) return "mastercard";
    if (/^506/.test(cleanNum)) return "verve";
    return "mastercard";
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const cleanNumber = cardNumber.replace(/\s/g, "");

    if (!cardName.trim()) {
      toast.error("Please enter the cardholder name");
      return;
    }

    if (cleanNumber.length < 16) {
      toast.error("Please enter a valid 16-digit card number");
      return;
    }

    if (!expiry || expiry.length < 5) {
      toast.error("Please enter a valid expiry date (MM/YY)");
      return;
    }

    const [month, year] = expiry.split("/");
    const numMonth = parseInt(month, 10);
    if (isNaN(numMonth) || numMonth < 1 || numMonth > 12) {
      toast.error("Invalid expiry month");
      return;
    }

    if (!cvv || cvv.length < 3) {
      toast.error("Please enter a valid CVV");
      return;
    }

    setIsSubmitting(true);

    setTimeout(() => {
      const brand = getCardBrand(cleanNumber);
      const newCard = {
        id: `card_${Date.now()}`,
        brand: brand,
        last4: cleanNumber.slice(-4),
        expiry: expiry,
        bankName: brand === "visa" ? "Visa Debit" : "Mastercard Debit",
        cardholder: cardName.trim(),
        isDefault: false,
      };

      if (onCardAdded) {
        onCardAdded(newCard);
      }

      toast.success("Card added successfully");
      setIsSubmitting(false);

      // Reset form
      setCardNumber("");
      setExpiry("");
      setCvv("");
      setCardName("");

      handleClose();
    }, 400);
  };

  const detectedBrand = getCardBrand(cardNumber);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 300,
        sx: { backgroundColor: "rgba(0, 0, 0, 0.55)" },
      }}
    >
      <Fade in={open}>
        <Box sx={modalStyle}>
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[18px] font-[700] text-[#1E222B]">
              Add New Card
            </h2>
            <div
              onClick={handleClose}
              className="w-8 h-8 rounded-full bg-[#18191B] hover:bg-black text-white flex items-center justify-center cursor-pointer transition shadow-xs"
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Cardholder Name */}
            <div>
              <label className="block text-[13px] font-[600] text-[#1E222B] mb-1.5">
                Full Name
              </label>
              <input
                type="text"
                placeholder="e.g. John Doe"
                value={cardName}
                onChange={(e) => setCardName(e.target.value)}
                required
                className="w-full bg-[#FAFBFC] border border-gray-200 rounded-xl px-4 py-3 text-[14px] text-[#1E222B] placeholder:text-gray-400 focus:outline-none focus:border-[#00BC67] transition"
              />
            </div>

            {/* Card Number */}
            <div>
              <label className="block text-[13px] font-[600] text-[#1E222B] mb-1.5">
                Card Number
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="0000 0000 0000 0000"
                  value={cardNumber}
                  onChange={handleCardNumberChange}
                  required
                  maxLength={19}
                  className="w-full bg-[#FAFBFC] border border-gray-200 rounded-xl pl-4 pr-12 py-3 text-[14px] font-mono text-[#1E222B] placeholder:text-gray-400 focus:outline-none focus:border-[#00BC67] transition"
                />
                <div className="absolute right-3.5 top-1/2 -translate-y-1/2">
                  {detectedBrand === "visa" ? (
                    <span className="text-[10px] font-black italic tracking-tighter text-[#1A1F71] bg-gray-100 px-1 py-0.5 rounded border border-gray-200">
                      VISA
                    </span>
                  ) : (
                    <div className="flex items-center -space-x-1">
                      <div className="w-3.5 h-3.5 rounded-full bg-[#EB001B]" />
                      <div className="w-3.5 h-3.5 rounded-full bg-[#F79E1B] opacity-90" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Expiry & CVV */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[13px] font-[600] text-[#1E222B] mb-1.5">
                  Expiry (MM/YY)
                </label>
                <input
                  type="text"
                  placeholder="MM/YY"
                  value={expiry}
                  onChange={handleExpiryChange}
                  required
                  maxLength={5}
                  className="w-full bg-[#FAFBFC] border border-gray-200 rounded-xl px-4 py-3 text-[14px] font-mono text-[#1E222B] placeholder:text-gray-400 focus:outline-none focus:border-[#00BC67] transition text-center"
                />
              </div>

              <div>
                <label className="block text-[13px] font-[600] text-[#1E222B] mb-1.5">
                  CVV
                </label>
                <input
                  type="password"
                  placeholder="•••"
                  value={cvv}
                  onChange={handleCvvChange}
                  required
                  maxLength={4}
                  className="w-full bg-[#FAFBFC] border border-gray-200 rounded-xl px-4 py-3 text-[14px] font-mono text-[#1E222B] placeholder:text-gray-400 focus:outline-none focus:border-[#00BC67] transition text-center"
                />
              </div>
            </div>

            {/* Security note */}
            <div className="flex items-center gap-1.5 text-gray-500 pt-1">
              <LockOutlinedIcon sx={{ fontSize: 14, color: "#00BC67" }} />
              <span className="text-[11px]">
                Your card information is securely encrypted
              </span>
            </div>

            {/* Submit Button */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3.5 rounded-xl bg-[#00BC67] hover:bg-[#00a85c] disabled:opacity-60 text-white font-[700] text-[15px] cursor-pointer transition shadow-xs text-center"
              >
                {isSubmitting ? "Adding Card..." : "Add Card"}
              </button>
            </div>
          </form>
        </Box>
      </Fade>
    </Modal>
  );
}
